/** @jsxImportSource @emotion/react */
import type { ColDef } from 'ag-grid-community'
import { t } from '@sportninja/common/i18n'
import DataGrid from 'src/components/ts-components/DataGrid'
import {
  OPERATIONS_GRID_HEADER_HEIGHT,
  OPERATIONS_GRID_ROW_HEIGHT,
  operationsGridStyle,
} from '../operationsGridStyle'
import RosterGridSkeleton from './RosterGridSkeleton'
import { rosterGridSkeletonColumnCount } from './rosterGridSkeletonColumnCount'

const translate = t as (key: string, options?: unknown) => string

interface RosterGridProps<TRow extends { id: string }> {
  rows: TRow[]
  columns: ColDef<TRow>[]
  /** Rows with a save in flight, dimmed until the server answers. */
  pendingIds: Set<string>
  loading: boolean
  testId: string
  /** Shown when there are no rows — names what is missing. */
  emptyLabel?: string
}

/**
 * One roster table. Shared by Players, Spares and Team Officials so all three
 * carry the same theme, pending treatment and empty state.
 *
 * Rows are not clickable: unlike the division/season grids there is nothing to
 * drill into, and a stray click must never interrupt an inline edit.
 */
const RosterGrid = <TRow extends { id: string }>({
  rows,
  columns,
  pendingIds,
  loading,
  testId,
  emptyLabel,
}: RosterGridProps<TRow>) => {
  'use memo'

  if (loading) {
    // Rows-in-flight replace ag-grid's own spinner-over-empty-grid overlay
    // with a skeleton shaped like the table about to arrive — see
    // RosterGridSkeleton. `testId` moves onto the skeleton itself rather than
    // staying on `DataGrid`, which does not mount while loading is true.
    return (
      <RosterGridSkeleton
        columnCount={rosterGridSkeletonColumnCount(columns)}
        testId={testId}
      />
    )
  }

  return (
    <div css={operationsGridStyle}>
      <DataGrid<TRow>
        rowData={rows}
        columnDefs={columns}
        getRowId={({ data }) => data.id}
        domLayout='autoHeight'
        stickyHorizontalScrollbar
        headerHeight={OPERATIONS_GRID_HEADER_HEIGHT}
        rowHeight={OPERATIONS_GRID_ROW_HEIGHT}
        rowClassRules={{
          'operations-row-pending': ({ data }) =>
            Boolean(data && pendingIds.has(data.id)),
        }}
        overlayNoRowsTemplate={
          emptyLabel ?? translate('DiscoverScreen:noResults')
        }
        data-testid={testId}
      />
    </div>
  )
}

export default RosterGrid
